import type React from 'react';
import { useCallback, useEffect, useState } from 'react';

/** How long the arrival highlight stays on the deep-linked cell before fading back out. */
export const HIGHLIGHT_DURATION_MS = 7500;
/**
 * The cell classes an arrival alternates between. Each carries the same fade under its own
 * keyframes name: a cell still fading from the last arrival only restarts its animation when the
 * animation name changes, so a second arrival on that cell takes the other class.
 */
export const HIGHLIGHT_CELL_CLASSES: readonly [string, string] = [
  'Report-highlightCell',
  'Report-highlightCellRepeat',
];

/**
 * The slice of the MUI grid API this hook drives. Structural instead of the full `GridApi` so
 * tests pass a small stub without casts, and so the hook is honest about what it depends on.
 */
export interface ScrollToCellGridApi {
  getAllColumns: () => { field: string }[];
  getColumnIndex: (field: string) => number;
  getRowsCount: () => number;
  scrollToIndexes: (params: { colIndex: number; rowIndex: number }) => void;
  /** `rows.loading` is true from a filter or sort change until the refetched first page lands. */
  state: { rows: { loading?: boolean } };
  subscribeEvent: (event: 'rowsSet', handler: () => void) => () => void;
}

/** A grid cell, by row id and the grid's own column field. */
export interface ReportGridCell {
  documentId: string;
  field: string;
}

/** The cell a deep link landed on, the class that flashes it, and when the flash ends. */
export interface CellHighlight extends ReportGridCell {
  className: string;
  expiresAt: number;
}

/**
 * Scrolls the report grid to a deep-linked cell and returns the highlight for it. The target
 * arrives on the URL as `?scrollToDoc=<documentId>&highlightCol=<field>`, which is how a chat
 * citation into a report cell lands.
 *
 * The highlight is state, not a class added to the DOM: the grid virtualises and re-renders its
 * cells, so a class it does not own is lost on the next render. The caller hands the result to
 * `getCellClassName` through {@link getCellHighlightClassName}, and the cell carries the class
 * whenever it renders, however late the scroll brings it in.
 *
 * The target is read live from the URL by the caller, never captured once at mount: a citation
 * into a report that is already open navigates the existing tab to the new params instead of
 * mounting a fresh grid, and a mount-time capture would keep serving the first citation forever.
 *
 * That is also why waiting for `rowsSet` is not enough on its own. On a cold load the rows
 * arrive after this effect runs, so the hook waits for them. Into an already-open report they
 * are already there, and `rowsSet` would not fire again until the next fetch, so when the grid
 * has rows the lookup starts at once. The `rowsSet` subscription stays in place until a lookup
 * succeeds, so a document the grid could not place yet gets another try when rows next change.
 *
 * A lookup only counts once the grid holds the row it names. A cold load fires `rowsSet` while
 * the grid is still empty, and a scroll then is a silent no-op: taking it as success would
 * consume the params and leave the user at the top of the report.
 *
 * Nor does it count while the grid is loading. A filter or sort change (including the grid
 * normalising the saved filter model on mount) refetches the first page, and the grid scrolls
 * back to the top when it lands, undoing a scroll made against the rows it replaced. The hook
 * waits for the `rowsSet` that ends the load. The grid publishes it before it clears `loading`
 * in the same call, so the retry runs a microtask later.
 *
 * `resolveRowIndex` locates the document under the grid's current filter and sort, returning
 * `null` when it is not in the report. `onTargetReached` clears the params: the re-render that
 * causes retires the effect (a run with no target is a no-op), and it is what lets the same
 * citation be clicked a second time. Changing the target while a lookup is still pending
 * cancels that lookup, so a stale answer cannot scroll to the wrong cell.
 *
 * React.Activity re-runs effects on every tab re-show. Once the params are consumed the scroll
 * effect is a no-op, and the expiry timer resumes from the highlight's fixed end time.
 */
export function useScrollToReportCell({
  apiRef,
  documentId,
  field,
  onTargetReached,
  resolveRowIndex,
}: {
  apiRef: React.RefObject<ScrollToCellGridApi | null>;
  documentId: string | null;
  field: string | null;
  onTargetReached: () => void;
  resolveRowIndex: (documentId: string) => Promise<number | null>;
}): CellHighlight | null {
  const [highlight, setHighlight] = useState<CellHighlight | null>(null);
  const onCellReached = useCallback((cell: ReportGridCell) => {
    setHighlight(previous => ({
      ...cell,
      className: getNextHighlightClass(previous),
      expiresAt: Date.now() + HIGHLIGHT_DURATION_MS,
    }));
  }, []);

  useEffect(() => {
    const api = apiRef.current;
    if (!documentId || !field || !api) return;
    return startScrollToCell({
      api,
      documentId,
      field,
      onCellReached,
      onTargetReached,
      resolveRowIndex,
    });
  }, [apiRef, documentId, field, onCellReached, onTargetReached, resolveRowIndex]);

  useEffect(() => {
    if (!highlight) return;
    const timeout = setTimeout(
      () => setHighlight(current => (current === highlight ? null : current)),
      Math.max(0, highlight.expiresAt - Date.now())
    );
    return () => clearTimeout(timeout);
  }, [highlight]);

  return highlight;
}

/** The class for one grid cell under the current highlight, for the grid's `getCellClassName`. */
export function getCellHighlightClassName(
  highlight: CellHighlight | null,
  cell: { field: string; id: number | string }
): string {
  if (!highlight) return '';
  return cell.id === highlight.documentId && cell.field === highlight.field
    ? highlight.className
    : '';
}

function getNextHighlightClass(previous: CellHighlight | null): string {
  const [first, second] = HIGHLIGHT_CELL_CLASSES;
  return previous?.className === first ? second : first;
}

/**
 * One attempt to reach the cell, started as soon as the grid has settled rows and again on every
 * `rowsSet` until the document is placed. Returns the cleanup that cancels it.
 */
function startScrollToCell({
  api,
  documentId,
  field,
  onCellReached,
  onTargetReached,
  resolveRowIndex,
}: {
  api: ScrollToCellGridApi;
  documentId: string;
  field: string;
  onCellReached: (cell: ReportGridCell) => void;
  onTargetReached: () => void;
  resolveRowIndex: (documentId: string) => Promise<number | null>;
}): () => void {
  let cancelled = false;
  let lookupInFlight = false;
  let unsubscribe: (() => void) | null = null;

  const getIsGridSettled = () => api.getRowsCount() > 0 && !api.state.rows.loading;

  async function scrollToCell() {
    if (cancelled || lookupInFlight || !getIsGridSettled()) return;
    lookupInFlight = true;
    let rowIndex: number | null;
    try {
      rowIndex = await resolveRowIndex(documentId);
    } finally {
      lookupInFlight = false;
    }
    if (cancelled || rowIndex === null || !getIsGridSettled() || rowIndex >= api.getRowsCount())
      return;

    unsubscribe?.();
    unsubscribe = null;

    const column = api
      .getAllColumns()
      .find(({ field: columnField }) => columnField.toLowerCase() === field.toLowerCase());
    onTargetReached();
    if (!column) return;

    api.scrollToIndexes({ colIndex: api.getColumnIndex(column.field), rowIndex });
    onCellReached({ documentId, field: column.field });
  }

  unsubscribe = api.subscribeEvent('rowsSet', () => queueMicrotask(() => void scrollToCell()));
  void scrollToCell();

  return () => {
    cancelled = true;
    unsubscribe?.();
  };
}
